import { getDepositProductsInfoList, getDepositProductsLogList } from '@/services/api.service';
import { getFrontEndName } from '@/utils/productName.utils';

// 取出 data payload 並檢查錯誤
function extractList(res, key) {
    const payload = res?.data?.data;
    if (!payload || payload.error_code !== 0) {
        throw payload?.error_msg || payload?.err_msg || '資料讀取失敗';
    }
    return payload[key] || [];
}

// 已領取判斷：take；舊資料 change_type 為 null 時 take_count <= 0 視為領取
function isTakeLog(log) {
    if (log.change_type === 'take') return true;
    if (log.change_type == null) return Number(log.take_count) <= 0;
    return false;
}

// 由 order_number（G+YYMMDD…）解析買單日期
function dateFromOrderNumber(orderNumber) {
    const m = String(orderNumber || '').match(/(\d{2})(\d{2})(\d{2})/);
    return m ? `20${m[1]}/${m[2]}/${m[3]}` : '未知日期';
}

// created_at 為 UTC naive，+8 轉台北日期
function dateFromCreatedAt(createdAt) {
    if (!createdAt) return '未知日期';
    const d = new Date(`${createdAt}Z`);
    d.setUTCHours(d.getUTCHours() + 8);
    const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
    const da = String(d.getUTCDate()).padStart(2, '0');
    return `${d.getUTCFullYear()}/${mo}/${da}`;
}

// 將同一套餐（parent_combo_line_id）的品項收進同一個 row，保留出現順序
// 回傳 row：{ comboName, comboCount, items[] }；單品 comboName 為 null、items 只有一筆
// 套數推算見分析文件第六點：先把同一子品項（combo_slot_key）的個數加總，
// 套數 = 木桶效應 min( 子項個數 ÷ count_per_combo )，整數除法。
function buildRows(items) {
    const rows = [];
    const comboMap = {};
    items.forEach((it) => {
        const lineId = it.parentComboLineId;
        if (!lineId) {
            rows.push({ comboName: null, items: [it] });
            return;
        }
        if (!comboMap[lineId]) {
            comboMap[lineId] = { comboName: it.comboName, subMap: {}, items: [] };
            rows.push(comboMap[lineId]);
        }
        const group = comboMap[lineId];
        // 同一子品項（同 slot）可能有多筆（例如多次領取），個數需加總
        const subKey = it.comboSlotKey || it.productId;
        if (!group.subMap[subKey]) {
            group.subMap[subKey] = { ...it, count: 0 };
            group.items.push(group.subMap[subKey]);
        }
        group.subMap[subKey].count += it.count;
    });
    rows.forEach((row) => {
        if (!row.comboName) return;
        delete row.subMap;
        // 木桶效應：被最少的子項卡住
        row.comboCount = row.items.reduce((min, sub) => {
            const sets = Math.floor(sub.count / (sub.countPerCombo || 1));
            return Math.min(min, sets);
        }, Infinity);
        if (!Number.isFinite(row.comboCount)) row.comboCount = 0;
    });
    return rows;
}

// 依日期分組，組內再依套餐分 row；日期由新到舊
function groupBy(list, getDate, getCount) {
    const map = {};
    list.forEach((item) => {
        const date = getDate(item);
        (map[date] = map[date] || []).push({
            productId: item.product_id,
            name: getFrontEndName(item.product_name),
            spec: item.description,
            count: getCount(item),
            countPerCombo: Number(item.count_per_combo) || 1,
            comboSlotKey: item.combo_slot_key || null,
            parentComboLineId: item.parent_combo_line_id || null,
            comboName: item.parent_combo_name ? getFrontEndName(item.parent_combo_name) : null,
        });
    });
    return Object.keys(map)
        .sort((a, b) => b.localeCompare(a))
        .map((date) => ({ date, rows: buildRows(map[date]) }));
}

export default {
    namespaced: true,
    state: {
        unclaimedList: [],
        claimedList: [],
        loaded: { unclaimed: false, claimed: false },
        loading: false,
    },
    getters: {
        unclaimedGroups: (state) =>
            groupBy(
                state.unclaimedList,
                (i) => dateFromOrderNumber(i.order_number),
                (i) => Number(i.deposit_count) || 0
            ),
        claimedGroups: (state) =>
            groupBy(
                state.claimedList,
                (i) => dateFromCreatedAt(i.created_at),
                (i) => Math.abs(Number(i.take_count) || 0)
            ),
    },
    mutations: {
        setUnclaimedList(state, list) { state.unclaimedList = list; },
        setClaimedList(state, list) { state.claimedList = list; },
        setLoaded(state, { tab, value }) { state.loaded[tab] = value; },
        setLoading(state, value) { state.loading = value; },
        reset(state) {
            state.unclaimedList = [];
            state.claimedList = [];
            state.loaded = { unclaimed: false, claimed: false };
        },
    },
    actions: {
        async fetchUnclaimed({ commit, rootGetters }) {
            commit('setLoading', true);
            try {
                const res = await getDepositProductsInfoList({
                    store_member_uuid: rootGetters['userData/storeMemberUuid'],
                    page: 0,
                    page_size: 999,
                });
                commit('setUnclaimedList', extractList(res, 'deposit_prods_info_list'));
                commit('setLoaded', { tab: 'unclaimed', value: true });
            } finally {
                commit('setLoading', false);
            }
        },
        async fetchClaimed({ commit, rootGetters }) {
            commit('setLoading', true);
            try {
                const res = await getDepositProductsLogList({
                    store_member_uuid: rootGetters['userData/storeMemberUuid'],
                    page: 0,
                    page_size: 999,
                });
                commit('setClaimedList', extractList(res, 'deposit_prods_log_list').filter(isTakeLog));
                commit('setLoaded', { tab: 'claimed', value: true });
            } finally {
                commit('setLoading', false);
            }
        },
    },
};
